import {useContext, useLayoutEffect, useRef} from 'react';

import {DashboardAdaptiveDensityContext} from '../contexts/DashboardAdaptiveDensityContext';
import {DashboardAdaptiveDensityParticipant} from '../types/DashboardAdaptiveDensityParticipant';

/**
 * Registers a tile's rows with the board's adaptive density coordinator and reports every change
 * to it. Returns whether a coordinator is present.
 *
 * The coordinator measures the rendered rows outside React, so each hand-off runs in a layout
 * effect: after React commits the rows it has to measure and before the browser paints them, which
 * keeps a density change from flashing at the old row count.
 */
export function useDashboardAdaptiveDensityParticipant(
  participant: DashboardAdaptiveDensityParticipant
) {
  const coordinator = useContext(DashboardAdaptiveDensityContext);
  const participantRef = useRef(participant);
  const visibleRows = participant.getVisibleRows();
  const probeRow = participant.getProbeRow();

  // The coordinator calls back into the participant between renders, so the ref it reads must hold
  // the closures of the latest committed render.
  useLayoutEffect(function syncParticipantRef() {
    participantRef.current = participant;
  });

  useLayoutEffect(
    function registerAdaptiveRows() {
      if (coordinator === null) {
        return undefined;
      }

      return coordinator.register(participantRef.current);
    },
    [coordinator, participant.id]
  );

  useLayoutEffect(
    function updateAdaptiveRows() {
      coordinator?.update(participantRef.current);
    },
    [coordinator, participant.layoutSignature, probeRow, participant.id, visibleRows]
  );

  return coordinator !== null;
}
